<script lang="ts">
  import '../app.css';
  import { onMount } from 'svelte';

  import { initializeFirebase, isFirebaseReady } from '$lib/firebase/client';
  import { initAuthProvider } from '$lib/platform/auth-provider';
  import { initAuthPersistence } from '$lib/firebase/auth';
  import { QueryParameterHandler } from '$lib/services/QueryParameterHandler';
  import { fetchRemoteConfig } from '$lib/firebase/remote-config';
  import { PdfCacheService } from '$lib/services/PdfCacheService';
  import { createAuthStore, AuthState } from '$lib/stores/authStore.svelte';
  import { appStore } from '$lib/stores/appStore.svelte';
  import AppLayout from '$lib/components/AppLayout.svelte';
  import LoadingSpinner from '$lib/components/LoadingSpinner.svelte';
  import ErrorMessage from '$lib/components/ErrorMessage.svelte';
  import RootDialog from '$lib/components/Dialog/RootDialog.svelte';
  import { clearAppCaches, needsCacheMigration, persistCacheSchemaVersion } from '$lib/services/clearAppCaches';
  import { isTauri } from '$lib/platform/detector';
  import { t as __t, onLocaleChange } from '$lib/i18n';

  let { children }: { children: import('svelte').Snippet } = $props();

  let initError = $state<string | null>(null);
  let initialized = $state(false);
  let updateAvailable = $state(false);

  let _lcv = $state(0);
  $effect(() => { const unsub = onLocaleChange(() => _lcv++); return unsub; });
  function _(key: string, params?: Record<string, string | number>): string { _lcv; return __t(key, params); }

  onMount(async () => {
    try {
      // --- Report recovery from chunk error to Sentry ---
      try {
        if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('had-chunk-error') === 'true') {
          sessionStorage.removeItem('had-chunk-error');
          import('@sentry/browser').then((Sentry) => {
            Sentry.captureMessage('App recovered from a stale chunk error after auto-reload', {
              level: 'info',
              tags: { recovered_from_chunk_error: 'true' }
            });
          }).catch((err) => {
            console.error('Failed to report recovery to Sentry:', err);
          });
        }
      } catch (e) {
        // Safe fallback
      }

      // --- Domain redirect (banshee -> app) ---
      const host = window.location.hostname;
      if (
        host === 'banshee.universalvoice.nl' ||
        host === 'banshee-dev.universalvoice.nl'
      ) {
        const isDev = host.includes('-dev');  
        const newHost = isDev
          ? 'app-dev.universalvoice.nl'
          : 'app.universalvoice.nl';

        alert(
          'This domain is deprecated. Please use ' +
            newHost +
            ' instead. Your login information is not affected.'+
            host+' -> '+newHost,
        );

        window.location.href =
          'https://' +
          newHost +
          window.location.pathname +
          window.location.search;
        return;
      }

      // --- Cache migration on schema change ---
      // Bump CACHE_SCHEMA_VERSION in clearAppCaches.ts only when the
      // underlying cache format changes (e.g. Svelte 4→5 upgrade).
      // Normal deploys skip this so user caches are preserved.
      if (needsCacheMigration()) {
        await clearAppCaches();
        persistCacheSchemaVersion();
      }

      // --- Initialize Firebase ---
      await initializeFirebase();

      // --- Auth persistence ---
      await initAuthPersistence();

      // --- Setup query parameter handlers ---
      const queryHandler = new QueryParameterHandler();
      await queryHandler.handleQueryParameters();

      // --- Auth provider ---
      // The auth-store is initialised by AppLayout via createAuthStore()
      initAuthProvider();

      // --- Remote config (best-effort) ---
      fetchRemoteConfig().catch(() => {
        // Non-fatal
      });

      // --- Initialize IndexedDB cache for offline PDF support ---
      PdfCacheService.init().catch((error) => {
        console.error("Failed to initialize PDF cache:", error);
        // Non-fatal error - offline caching is optional
      });

      // --- Service worker update detection (web only) ---
      // With registerType: 'autoUpdate', a new SW auto-installs and takes
      // over.  On controllerchange the new version's assets are ready, but
      // the running page still has old code — show a toast inviting the
      // user to reload.
      if ('serviceWorker' in navigator && !isTauri()) {
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          updateAvailable = true;
        });
      }

      initialized = true;
    } catch (e) {
      console.error('App initialization failed:', e);
      initError = 'Failed to initialize application. Please try again later.';
    }
  });
</script>

{#if initError}
  <ErrorMessage message={initError} />
{:else}
  <AppLayout initializing={!initialized || !isFirebaseReady()}>
    {#if !initialized || !isFirebaseReady()}
      <LoadingSpinner message="Initializing..." />
    {:else}
      {@render children()}
    {/if}
  </AppLayout>
{/if}

{#if updateAvailable}
  <div class="fixed top-0 left-0 right-0 z-[9999] bg-primary-500 text-white px-4 py-3 shadow-lg flex items-center justify-between gap-3 text-sm">
    <span>{_('app.update_available')}</span>
    <button
      onclick={() => window.location.reload()}
      class="shrink-0 font-semibold bg-white text-primary-500 px-4 py-1.5 rounded-md hover:bg-primary-50 transition-colors"
    >
      {_('app.update_reload')}
    </button>
  </div>
{/if}

<RootDialog />
